import ReactGA from "react-ga4";
import posthog from "posthog-js";
import { getVersionInfo } from "./configMgr";

/**
 * Analytics — GA4 event tracking abstraction.
 *
 * ## Usage
 *
 *   import Analytics from "../lib/Analytics";
 *   Analytics.event(Analytics.EVENTS.TAB_SELECT, { category: "target" });
 *
 * All event names are defined in EVENTS (kebab-case strings). Each call passes
 * a plain JS payload object whose fields become GA4 custom parameters.
 * Data is sent as-is — no redaction is applied.
 *
 * ## Event reference
 *
 * ### Search / Results
 *   search              query, elapsed          SearchSessionStore._runNewSearch
 *   autosuggest         query, elapsed          SearchSessionStore.fetchSuggestions
 *   tab-select          category                SearchSessionStore.currentCategory setter
 *   filter-change       query                   SearchResults.handleFilterInputChange
 *   dataset-filter      datasets, category      SearchResults.handleDatasetsFilterChange
 *   result-click        result_id, category     SearchResults.handleShowEvidence
 *   export              format, search_url,     SearchSessionStore.exportSearchResultsAsFormat
 *                       result_count
 *   settings-change     setting, value          SettingsMenu onChange handlers
 *
 * ### Graph
 *   graph-node-click    node_id, node_label     EdgeGraph.handleNodeClick
 *   graph-edge-click    source, target          EdgeGraph.handleEdgeClick
 *   graph-config        setting, value          SearchResults graph control onChange handlers
 *
 * ### Chat
 *   chat-start          agent, conversation_id  ChatSession.sendChatMessage (first turn)
 *   chat-end            agent, conversation_id, ChatSession.discardConversation
 *                       turn_count
 *   chat-turn-sent      agent, turn_index,      ChatSession.sendChatMessage
 *                       conversation_id, conversation_link
 *   chat-submit-perf    serialize_ms,           LLMClient._sendPrompt (every submit)
 *                       turn_count, canonical_count, stream_id
 *   chat-turn-received  agent, turn_index,      ChatSession busy→idle effect
 *                       conversation_id, conversation_link
 *   chat-export         conversation_id, format ChatDownloadMenu download handlers
 *   chat-copy           conversation_id         ChatSession CopyToClipboardButton
 *   chat-compact        agent, conversation_id  ChatSession.handleCompact
 *   chat-interrupt      agent, conversation_id  ChatSession stop button
 *   chat-bug-report     agent, conversation_id  ChatSession.handleBugReportSubmit
 *   chat-mode-switch    mode                    App.js chat/search toggle
 *   chat-sources-toggle open                    ChatSession sources drawer button
 *
 * ### Investigate (Evidence Chains)
 *   evidence-chains     target_id               SearchResults.handleShowEvidence
 *   evidence-chains-complete  target_id,        EChainStore.getEChains (success)
 *                       duration_ms, path_count
 *   evidence-chains-error  target_id,           EChainStore.getEChains (failure)
 *                       duration_ms, error
 *   evidence-chains-cancel  target_id,          EChainStore.cancelFetch (modal closed while loading)
 *                       duration_ms
 *   echain-export       target_id               EvidenceModal.handleExportClick
 *   echain-expand       target_id, group        EvidenceModal.handleGroupToggle
 *   compound-similarity target_id, group,       EvidenceModal.handleComparisonToggle
 *                       chain, edge
 *
 * ### History
 *   history-item-load      item_id, item_type,     HistoryDrawer.handleLoadItem
 *                          title
 *   history-delete         item_id, item_type      HistoryStore.deleteItem
 *   history-load-more      type                    HistoryStore.loadMore
 *   history-drawer-toggle  open, mode              HistoryDrawer.toggleDrawer
 *   history-new-item       mode, new_tab           HistoryDrawer.handleNewItem
 *   history-rename         item_id, item_type      HistoryDrawer.handleSaveEdit
 *   history-duplicate      item_id, item_type      HistoryDrawer.handleDuplicateItem
 *   history-regen-title    item_id, item_type      HistoryDrawer.handleRegenerateTitle
 *   history-move-item      item_id, item_type,     HistoryDrawer.handleMoveItem
 *                          project_id
 *   history-drag-drop      item_id, project_id     HistoryDrawer.handleDropOnProject
 *   history-bulk-move      count, project_id       HistoryDrawer.handleBulkMove
 *   history-bulk-delete    count, item_type        HistoryDrawer.handleBulkDelete
 *   history-clear          item_type               HistoryDrawer.handleConfirmClear
 *   history-open-new-tab   item_id, item_type      HistoryDrawer.handleOpenInNewTab
 *   history-project-create (none)                  HistoryDrawer project creation
 *   history-project-delete project_id, with_items  HistoryDrawer project delete handlers
 *   history-project-toggle project_id, open        HistoryDrawer.toggleProject
 *   history-project-color  project_id              HistoryDrawer project color change
 *
 * ### User / Auth
 *   signin              user, tenant            UserStore (+ identify())
 *   signin-failure      user, error_name        UserStore login catch
 *   signout             user                    UserStore
 *   session-restore     user, tenant            UserStore (+ identify())
 *   register            user                    UserStore
 *   register-failure    user, error_name        UserStore register catch
 *   confirm-registration user                   UserStore
 *   resend-confirmation  user                   UserStore
 *   password-reset      user                    UserStore
 *   password-change     user                    UserStore
 *
 * ### Molecular Editor
 *   ketcher-open        (none)                  MolecularEditorModal
 *   ketcher-search      smiles                  MolecularEditorModal
 *   ketcher-add-to-chat smiles                  ChatSession.handleMolEditorAdd
 *
 * ### Streaming transport
 *   streaming-connected connect_ms              useStreamConnector (first byte received, attempt 0)
 *   streaming-fallback  elapsed_ms              useStreamConnector (SSE timeout → polling)
 *   streaming-recovered polling_duration_ms     useStreamConnector (recovery probe success)
 *   streaming-poll-tick poll_count, events_returned,  useStreamConnector (every 10th poll)
 *                       elapsed_ms, stream_id
 *   streaming-inter-chunk-timeout last_event_id useStreamConnector (proxy silent mid-stream drop, attempt 0)
 *   chat-cancel         agent, stream_id,       LLMClient._cancelAgents (hard cancel)
 *                       elapsed_ms, turn_count
 *   chat-content-filtered agent, agentId,       LLMClient message_stop handler
 *                       cell, user, tenant,
 *                       prompt
 *
 * ### Links
 *   outbound-link       label, ...props         OutboundLink component
 *
 * @module Analytics
 * @internal
 */

let _debug = false;
let _initialized = false;
let _postHogEnabled = false;

const _DIMENSIONS = {
  USER_ID: "userId",
  TENANT_ID: "dimension1"
};

export const EVENTS = {
  // Search
  SEARCH:               "search",
  AUTOSUGGEST:          "autosuggest",
  TAB_SELECT:           "tab-select",
  FILTER_CHANGE:        "filter-change",
  DATASET_FILTER:       "dataset-filter",
  RESULT_CLICK:         "result-click",
  EXPORT:               "export",

  // Graph
  GRAPH_NODE_CLICK:     "graph-node-click",
  GRAPH_EDGE_CLICK:     "graph-edge-click",
  GRAPH_CONFIG:         "graph-config",

  // Search
  SETTINGS_CHANGE:      "settings-change",

  // Chat
  CHAT_START:           "chat-start",
  CHAT_END:             "chat-end",
  CHAT_TURN_SENT:       "chat-turn-sent",
  CHAT_TURN_RECEIVED:   "chat-turn-received",
  AGENT_TURN_START:     "agent-turn-start",     // message_start per agent (root + sub-agents)
  AGENT_TURN_COMPLETE:  "agent-turn-complete",  // message_stop per agent
  CHAT_EXPORT:          "chat-export",
  CHAT_COPY:            "chat-copy",
  CHAT_INTERRUPT:       "chat-interrupt",
  CHAT_HISTORY_SAVE:    "chat-history-save",
  CHAT_BUG_REPORT:      "chat-bug-report",
  CHAT_MODE_SWITCH:     "chat-mode-switch",
  CHAT_SOURCES_TOGGLE:  "chat-sources-toggle",

  // Investigate
  ECHAINS:              "evidence-chains",
  ECHAINS_COMPLETE:     "evidence-chains-complete",
  ECHAINS_ERROR:        "evidence-chains-error",
  ECHAINS_CANCEL:       "evidence-chains-cancel",
  ECHAIN_EXPORT:        "echain-export",
  ECHAIN_EXPAND:        "echain-expand",
  COMPOUND_SIMILARITY:  "compound-similarity",

  // User
  SIGNIN:               "signin",
  SIGNIN_FAILURE:       "signin-failure",
  SIGNOUT:              "signout",
  SESSION_RESTORE:      "session-restore",
  REGISTER:             "register",
  REGISTER_FAILURE:     "register-failure",
  CONFIRM_REGISTRATION: "confirm-registration",
  RESEND_CONFIRMATION:  "resend-confirmation",
  PASSWORD_RESET:       "password-reset",
  PASSWORD_CHANGE:      "password-change",
  HISTORY_ITEM_LOAD:      "history-item-load",
  HISTORY_DELETE:         "history-delete",
  HISTORY_LOAD_MORE:      "history-load-more",
  HISTORY_DRAWER_TOGGLE:  "history-drawer-toggle",
  HISTORY_NEW_ITEM:       "history-new-item",
  HISTORY_RENAME:         "history-rename",
  HISTORY_DUPLICATE:      "history-duplicate",
  HISTORY_REGEN_TITLE:    "history-regen-title",
  HISTORY_MOVE_ITEM:      "history-move-item",
  HISTORY_DRAG_DROP:      "history-drag-drop",
  HISTORY_BULK_MOVE:      "history-bulk-move",
  HISTORY_BULK_DELETE:    "history-bulk-delete",
  HISTORY_CLEAR:          "history-clear",
  HISTORY_OPEN_NEW_TAB:   "history-open-new-tab",
  HISTORY_PROJECT_CREATE: "history-project-create",
  HISTORY_PROJECT_DELETE: "history-project-delete",
  HISTORY_PROJECT_TOGGLE: "history-project-toggle",
  HISTORY_PROJECT_COLOR:  "history-project-color",

  // Molecular editor
  KETCHER_OPEN:         "ketcher-open",
  KETCHER_SEARCH:       "ketcher-search",
  KETCHER_ADD_TO_CHAT:  "ketcher-add-to-chat",

  // Developer docs
  DEVELOPER_PAGE_VIEW:      "developer-page-view",
  DEVELOPER_SECTION_VIEW:   "developer-section-view",
  DEVELOPER_TAB_SELECT:     "developer-tab-select",
  DEVELOPER_DOWNLOAD:       "developer-download",
  DEVELOPER_API_REF_CLICK:  "developer-api-ref-click",

  // Streaming transport
  STREAMING_CONNECTED:        "streaming-connected",        // SSE first byte received (initial connect success)
  STREAMING_FALLBACK:         "polling-fallback",           // SSE blocked, switched to polling
  STREAMING_RECOVERED:        "streaming-recovered",        // polling → streaming restored
  STREAMING_POLL_TICK:        "streaming-poll-tick",        // periodic polling heartbeat (every 10th poll)
  STREAMING_INTER_CHUNK_TIMEOUT: "streaming-inter-chunk-timeout", // proxy silently dropped mid-stream
  CHAT_STALE_EVENT:     "chat-stale-event",     // SSE event arrived after stream closed
  CHAT_CONTENT_FILTERED: "chat-content-filtered", // Bedrock content_filtered stop (no generation)
  CHAT_CONVERSATION_PASTE: "chat-conversation-paste", // user pasted conversation JSON into input
  CHAT_CONVERSATION_DROP:  "chat-conversation-drop",  // user dropped .plex/.json file onto chat

  // Chat lifecycle
  CHAT_CANCEL:          "chat-cancel",          // user hard-cancelled an active stream

  // Errors
  API_ERROR:            "api-error",

  // Links
  OUTBOUND_LINK:        "outbound-link",
};

const _log = (...args) => {
  if (_debug) {
    console.log("[Analytics]", ...args);
  }
};

const _warn = (...args) => {
  if (_debug) {
    console.warn("[Analytics]", ...args);
  }
};

const _error = (...args) => {
  console.error("[Analytics]", ...args);
};

// _redact is intentionally a no-op — data is sent as-is to GA4
const _redact = (str) => str ?? "";

export const initialize = (trackingID, history, options) => {
  if (!trackingID) {
    _warn("TrackingID is required");
    return;
  }
  if (_initialized) {
    _warn("Already initialized, skipping");
    return;
  }
  _initialized = true;

  _debug = options && options.debug;
  _log(trackingID);

  ReactGA.initialize(trackingID, {
    debug: false,
    titleCase: false,
    gaOptions: options && options.gaOptions
  });
  ReactGA.enable = options && options.enable;

  const ph = options?.postHog;
  if (ph?.token && ph?.enable) {
    posthog.init(ph.token, {
      api_host: ph.host || "https://us.i.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: false,
    });
    _postHogEnabled = true;
    const _registerNetworkProps = () => {
      const conn = navigator.connection;
      // navigator.connection available in Chrome/Edge; absent in Safari/Firefox
      if (!conn) return;
      posthog.register({
        network_type:           conn.type,           // "wifi" | "cellular" | "ethernet" | ...
        network_effective_type: conn.effectiveType,  // "4g" | "3g" | "2g" | "slow-2g"
      });
    };
    posthog.register({ app_version: getVersionInfo().version?.number });
    _registerNetworkProps();
    // Re-register on network change so mid-session switches (e.g. Novartis→hotspot) are captured
    navigator.connection?.addEventListener("change", _registerNetworkProps);
    // Pre-seed identity from last known user so events emitted before
    // identify() (e.g. pageview, pageleave) are attributed correctly.
    // Only applies when PostHog doesn't already have an identified user
    // (i.e. its persisted distinct_id looks like an anonymous UUID).
    const lastUser = localStorage.getItem("plex-last-username");
    const currentId = posthog.get_distinct_id?.();
    const isAnonymous = currentId && /^[0-9a-f-]{36}$/i.test(currentId);
    if (lastUser && isAnonymous) {
      posthog.identify(lastUser, { email: lastUser });
      _log("PostHog pre-seeded identity from last known user:", lastUser);
    }
    _log("PostHog initialized");

    // Long Tasks observer: report main-thread blocks >100ms to PostHog.
    // Helps diagnose UI lockups (e.g. during heavy MobX/React streaming updates).
    if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
      try {
        const _ltObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration >= 100) {
              posthog.capture('browser-long-task', {
                duration_ms: Math.round(entry.duration),
                start_time_ms: Math.round(entry.startTime),
              });
            }
          }
        });
        _ltObserver.observe({ type: 'longtask', buffered: false });
      } catch (_e) {
        // PerformanceObserver may throw in restricted environments — ignore
      }
    }
  }

  pageview(window.location.pathname, window.title);
  history?.listen(function(location) {
    pageview(location.pathname, location.title);
  });
  if(_debug) {
    window.analytics = { EVENTS, event };
  }
};

export const enable = (active) => {
  ReactGA.enable = active;
};

export const identify = (userID, tenantID) => {
  ReactGA.set({
    [_DIMENSIONS.USER_ID]: userID,
    [_DIMENSIONS.TENANT_ID]: tenantID
  });
  if (_postHogEnabled) {
    posthog.identify(userID, { email: userID, tenant: tenantID });
    // Bind the user to their tenant group so entitlement feature flags can
    // target by group (e.g. plex-cp ON for tenant=helix). Mirrors the backend
    // pattern where rd writes group properties after DynamoDB writes.
    if (tenantID) {
      posthog.group("tenant", tenantID);
    }
  }
};

export const pageview = (path, title = null) => {
  path = _redact(path);
  _log("pageview", path);
  ReactGA.set({ page: path });
  ReactGA.send({hitType: "pageview", page: path, title});
  if (_postHogEnabled) {
    posthog.capture("$pageview", { $current_url: path });
  }
};

export const event = (eventName, payload = {}) => {
  _log(eventName, payload);
  if (ReactGA.enable) {
    try {
      ReactGA.event(eventName, payload);
    } catch (error) {
      console.error("Analytics error", error);
    }
  }
  if (_postHogEnabled) {
    try {
      posthog.capture(eventName, payload);
    } catch (error) {
      console.error("PostHog error", error);
    }
  }
};

export const outboundLink = (props) => {
  const { label, ...otherProps } = props;
  event(EVENTS.OUTBOUND_LINK, { label, ...otherProps });
};

export const exception = (description, fatal) => {
  let descriptionWithStack = description.stack || Error(description).stack;
  _error("Error logged =>", descriptionWithStack);

  if (ReactGA.enable) {
    ReactGA.exception({
      description: descriptionWithStack,
      fatal: fatal
    })
  }
  if (_postHogEnabled) {
    try {
      posthog.capture("$exception", {
        $exception_message: description.message || String(description),
        $exception_stack_trace_raw: descriptionWithStack,
        fatal,
      });
    } catch (error) {
      console.error("PostHog exception capture error", error);
    }
  }
};

export default {
  initialize,
  enable,
  identify,
  exception,
  pageview,
  event,
  outboundLink,
  EVENTS,
  log: _log,
  warn: _warn,
  warning: _warn,
  error: _error
};

